import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface LegalDocument {
  id: string;
  doc_type: string;
  version: string;
  title: string;
  content_md: string;
  status: 'draft' | 'published' | 'archived';
  effective_from: string | null;
  published_at: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateLegalDocumentInput {
  doc_type: string;
  version: string;
  title: string;
  content_md: string;
  effective_from?: string | null;
}

export interface UpdateLegalDocumentInput {
  title?: string;
  content_md?: string;
  effective_from?: string | null;
}

/**
 * Hook for managing legal documents (admin only).
 */
export function useLegalDocuments(docType: string = 'producer_terms') {
  const queryClient = useQueryClient();

  // Fetch all documents of a specific type
  const { data: documents, isLoading, error, refetch } = useQuery({
    queryKey: ['legal-documents', docType],
    queryFn: async (): Promise<LegalDocument[]> => {
      const { data, error } = await supabase
        .from('legal_documents')
        .select('*')
        .eq('doc_type', docType)
        .order('published_at', { ascending: false, nullsFirst: false })
        .order('created_at', { ascending: false });

      if (error) {
        console.error('[useLegalDocuments] Error fetching documents:', error);
        throw error;
      }

      return (data || []) as LegalDocument[];
    },
  });

  // Create a new document (draft)
  const createMutation = useMutation({
    mutationFn: async (input: CreateLegalDocumentInput) => {
      const { data, error } = await supabase
        .from('legal_documents')
        .insert({
          doc_type: input.doc_type,
          version: input.version,
          title: input.title,
          content_md: input.content_md,
          effective_from: input.effective_from || null,
          status: 'draft',
        })
        .select()
        .single();

      if (error) {
        console.error('[useLegalDocuments] Error creating document:', error);
        throw error;
      }

      return data as LegalDocument;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['legal-documents', docType] });
    },
  });

  // Update a document
  const updateMutation = useMutation({
    mutationFn: async ({ id, ...input }: UpdateLegalDocumentInput & { id: string }) => {
      const { data, error } = await supabase
        .from('legal_documents')
        .update({
          ...input,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('[useLegalDocuments] Error updating document:', error);
        throw error;
      }

      return data as LegalDocument;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['legal-documents', docType] });
      queryClient.invalidateQueries({ queryKey: ['current-producer-terms'] });
    },
  });

  // Publish a document (and optionally archive previous)
  const publishMutation = useMutation({
    mutationFn: async ({ id, archivePrevious = true }: { id: string; archivePrevious?: boolean }) => {
      // If archiving previous, first update all other published docs to archived
      if (archivePrevious) {
        await supabase
          .from('legal_documents')
          .update({ status: 'archived' })
          .eq('doc_type', docType)
          .eq('status', 'published')
          .neq('id', id);
      }

      // Then publish this document
      const { data, error } = await supabase
        .from('legal_documents')
        .update({
          status: 'published',
          published_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('[useLegalDocuments] Error publishing document:', error);
        throw error;
      }

      return data as LegalDocument;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['legal-documents', docType] });
      queryClient.invalidateQueries({ queryKey: ['current-producer-terms'] });
      queryClient.invalidateQueries({ queryKey: ['producer-terms-status'] });
    },
  });

  // Archive a document
  const archiveMutation = useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await supabase
        .from('legal_documents')
        .update({
          status: 'archived',
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('[useLegalDocuments] Error archiving document:', error);
        throw error;
      }

      return data as LegalDocument;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['legal-documents', docType] });
      queryClient.invalidateQueries({ queryKey: ['current-producer-terms'] });
    },
  });

  // Delete a document (only drafts)
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('legal_documents')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('[useLegalDocuments] Error deleting document:', error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['legal-documents', docType] });
    },
  });

  // Get published document
  const publishedDocument = documents?.find(d => d.status === 'published') ?? null;
  const draftDocuments = documents?.filter(d => d.status === 'draft') ?? [];

  return {
    documents,
    publishedDocument,
    draftDocuments,
    isLoading,
    error,
    refetch,
    // Mutations
    createDocument: createMutation.mutateAsync,
    updateDocument: updateMutation.mutateAsync,
    publishDocument: publishMutation.mutateAsync,
    archiveDocument: archiveMutation.mutateAsync,
    deleteDocument: deleteMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isPublishing: publishMutation.isPending,
  };
}
